import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import {
  type CostObjectProjectRef,
  type CostObjectTag,
  projectTagFields,
  refineProjectTag,
} from './cost-objects.js'
import { money, toMoney } from './money.js'

// ── tax rates ──────────────────────────────────────────────────────────────────

/**
 * How supplies at a rate are reported on the VAT return. Standard supplies carry a positive rate;
 * zero-rated, exempt and out-of-scope supplies carry 0% but go to different boxes.
 */
export const TAX_CATEGORIES = ['STANDARD', 'ZERO', 'EXEMPT', 'OUT_OF_SCOPE'] as const
export const taxCategorySchema = z.enum(TAX_CATEGORIES)
export type TaxCategory = z.infer<typeof taxCategorySchema>

const taxRateFields = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  rate: z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter a percentage with up to 2 decimals')
    .refine((v) => Number(v) <= 100, 'A rate cannot exceed 100%'),
  /** Defaults to STANDARD for a positive rate, ZERO for 0%. */
  category: taxCategorySchema.optional(),
  isDefault: z.boolean().default(false),
})
const withCategory = <T extends { rate: string; category?: TaxCategory | undefined }>(v: T) => ({
  ...v,
  category: v.category ?? (Number(v.rate) > 0 ? ('STANDARD' as const) : ('ZERO' as const)),
})
const categoryMatchesRate = {
  path: ['category'],
  message: 'Standard rates are above 0%; zero-rated, exempt and out-of-scope rates are 0%',
}
const categoryOk = (v: { rate: string; category: TaxCategory }) =>
  (v.category === 'STANDARD') === Number(v.rate) > 0
export const taxRateInputSchema = taxRateFields
  .transform(withCategory)
  .refine(categoryOk, categoryMatchesRate)
export type TaxRateInput = z.input<typeof taxRateInputSchema>
export const taxRateUpdateSchema = taxRateFields
  .extend({ version: versionSchema })
  .transform(withCategory)
  .refine(categoryOk, categoryMatchesRate)

export interface TaxRate {
  id: string
  name: string
  nameAr: string | null
  rate: string
  category: TaxCategory
  isDefault: boolean
  archived: boolean
  version: number
}

/**
 * Tax on a net amount, rounded half away from zero to the baisa. Shared so the web app previews
 * exactly what the API will store.
 */
export function taxOn(netAmount: string, ratePercent: string): { tax: string; total: string } {
  const tax = toMoney(money(netAmount).times(ratePercent).dividedBy(100))
  return { tax, total: toMoney(money(netAmount).plus(tax)) }
}

// ── treasury ───────────────────────────────────────────────────────────────────

export const treasuryKindSchema = z.enum(['CASH', 'BANK', 'CARD_TERMINAL', 'MOBILE_WALLET'])
export type TreasuryKind = z.infer<typeof treasuryKindSchema>

const treasuryFields = {
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  kind: treasuryKindSchema,
  bankName: optionalText(100),
  accountNumber: optionalText(50),
  iban: optionalText(34),
}

export const treasuryAccountCreateSchema = z.object({
  ...treasuryFields,
  /** Money already in the account when it is set up; credited to opening balance equity. */
  openingBalance: moneySchema.default('0'),
  openingDate: z.iso.date().optional(),
})
export type TreasuryAccountCreate = z.input<typeof treasuryAccountCreateSchema>

export const treasuryAccountUpdateSchema = z.object({ ...treasuryFields, version: versionSchema })
export type TreasuryAccountUpdate = z.input<typeof treasuryAccountUpdateSchema>

/** What a form needs to offer a cash/bank account (no balances). */
export interface TreasuryAccountOption {
  id: string
  name: string
  nameAr: string | null
  kind: TreasuryKind
  /**
   * The bank details printed on documents (Settings › Printing picks the accounts to print without
   * `treasury.read`). Sent only to callers holding `settings.manage` or `treasury.read`; the other
   * pickers (cashiers, expense clerks) get the name and kind alone.
   */
  bankName?: string | null
  accountNumber?: string | null
  iban?: string | null
  /**
   * The active tills whose drawer this cash account is (none while POS is off). A drawer is the till's
   * own (BR-POS-004): receipts, refunds, payments and expenses in the back office leave it out. Absent
   * or empty: not a drawer.
   */
  drawerOf?: TillDrawerRef[]
}

/** A till whose drawer a cash account is. */
export interface TillDrawerRef {
  code: string
  name: string
  nameAr: string | null
}

/** Whether a picker's account is a till's drawer, which back-office documents leave out (BR-POS-004). */
export function isTillDrawer(account: { drawerOf?: readonly TillDrawerRef[] }): boolean {
  return (account.drawerOf?.length ?? 0) > 0
}

/**
 * A cash or bank account's master data, without its balance: what `settings.manage` maintains while
 * Accounting is off or out of reach (`GET /treasury-accounts/setup`, and the create and update
 * responses for a caller without `treasury.read`; ADR-024 D2).
 */
export interface TreasuryAccountRecord {
  id: string
  name: string
  nameAr: string | null
  kind: TreasuryKind
  glAccountId: string
  glAccountCode: string
  bankName: string | null
  accountNumber: string | null
  iban: string | null
  archived: boolean
  version: number
}

export interface TreasuryAccount extends TreasuryAccountRecord {
  /** Ledger balance of the backing GL account, today. */
  balance: string
}

export const treasuryTransferInputSchema = z
  .object({
    transferDate: z.iso.date(),
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
    reference: optionalText(100),
    notes: optionalText(500),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    path: ['toAccountId'],
    message: 'Choose a different account to transfer to',
  })
export type TreasuryTransferInput = z.input<typeof treasuryTransferInputSchema>

export interface TreasuryTransfer {
  id: string
  number: string
  transferDate: string
  from: { id: string; name: string }
  to: { id: string; name: string }
  amount: string
  reference: string | null
  notes: string | null
  status: 'POSTED' | 'VOID'
  voidReason: string | null
  /** A till pay-in or pay-out: cannot be voided from the back office (`TILL_DOCUMENT`). */
  fromTill: boolean
  createdAt: string
}

export const voidSchema = z.object({ reason: z.string().trim().min(1, 'Give a reason').max(300) })
export type VoidInput = z.input<typeof voidSchema>

// ── expenses ───────────────────────────────────────────────────────────────────

export const expenseCategoryInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  defaultAccountId: z.uuid(),
})
export type ExpenseCategoryInput = z.input<typeof expenseCategoryInputSchema>
export const expenseCategoryUpdateSchema = expenseCategoryInputSchema.extend({ version: versionSchema })

export interface ExpenseCategory {
  id: string
  name: string
  nameAr: string | null
  defaultAccount: { id: string; code: string; name: string }
  archived: boolean
  version: number
}

const expenseFields = z.object({
  expenseDate: z.iso.date(),
  categoryId: z.uuid(),
  /** Overrides the category's default account (expense or asset account). */
  accountId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  treasuryAccountId: z.uuid(),
  payee: optionalText(200),
  /**
   * The supplier whose tax invoice it is (G17): information only (nothing is posted to payables), for the
   * VAT return's input schedule and the supplier's VATIN. An archived supplier is refused (SUPPLIER_ARCHIVED).
   */
  supplierId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  reference: optionalText(100),
  description: z.string().trim().min(1, 'Describe the expense').max(500),
  /** Net of tax. */
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  taxRateId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /**
   * The project the expense is spent on (projects/02 §3). Its account and posting are unchanged; the
   * tag may move when the expense is edited (ASM-192).
   */
  ...projectTagFields,
})
export const expenseInputSchema = expenseFields.superRefine(refineProjectTag)
export type ExpenseInput = z.input<typeof expenseInputSchema>
export const expenseUpdateSchema = expenseFields
  .extend({ version: versionSchema })
  .superRefine(refineProjectTag)
export type ExpenseUpdate = z.input<typeof expenseUpdateSchema>

export const expenseListQuerySchema = pageQuerySchema.extend({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  categoryId: z.uuid().optional(),
  treasuryAccountId: z.uuid().optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  /** Expenses tagged to this project. */
  projectId: z.uuid().optional(),
})
export type ExpenseListQuery = z.input<typeof expenseListQuerySchema>

/**
 * `GET /expenses/summary`: the list's filters other than `status` (the status filter is what the
 * summary counts), paging and sort.
 */
export const expenseSummaryQuerySchema = expenseListQuerySchema.pick({
  q: true,
  from: true,
  to: true,
  categoryId: true,
  treasuryAccountId: true,
  projectId: true,
})
export type ExpenseSummaryQuery = z.input<typeof expenseSummaryQuerySchema>

/** One calendar month (`YYYY-MM`) of posted expenses. */
export interface ExpenseMonth {
  month: string
  count: number
  total: string
}

/**
 * The expense list's figures for the current filters. `all`, `posted` and `void` each equal the
 * `total` of the list call with that status (none, POSTED, VOID); `posted.total` and `posted.tax` are
 * the sums of those rows' `total` and `taxAmount`. `months`: posted expenses of the last 6 calendar
 * months (this one last, empty months included), following every filter but the dates. As of `asOf`,
 * today in Asia/Muscat.
 */
export interface ExpenseSummary {
  asOf: string
  all: { count: number }
  posted: { count: number; total: string; tax: string }
  void: { count: number }
  months: ExpenseMonth[]
}

export interface ExpenseListItem {
  id: string
  number: string
  expenseDate: string
  category: { id: string; name: string; nameAr: string | null }
  treasuryAccount: { id: string; name: string }
  payee: string | null
  description: string
  amount: string
  taxAmount: string
  total: string
  status: 'POSTED' | 'VOID'
  /** The project it is tagged to. Always sent by the API; optional in the type only for older fixtures. */
  project?: CostObjectProjectRef | null
}

/** The supplier an expense names, with its VATIN read live from the supplier record. */
export interface ExpenseSupplierRef {
  id: string
  code: string
  name: string
  /** The API always sends it; optional in the type only for older fixtures. */
  nameAr?: string | null
  taxNumber: string | null
}

export interface Expense extends ExpenseListItem {
  account: { id: string; code: string; name: string }
  /** The supplier it names (G17); null when none. Optional in the type only for older fixtures. */
  supplier?: ExpenseSupplierRef | null
  reference: string | null
  taxRateId: string | null
  taxRate: string
  voidReason: string | null
  /** Current (un-reversed) journal entry, null once voided. */
  journalEntryId: string | null
  /** The project tag; null when none. Always sent by the API; optional in the type only for older fixtures. */
  projectTag?: CostObjectTag | null
  /**
   * The recurring expense it is an occurrence of (created by it, or linked as its first date); null when
   * none. Always sent by the API; optional in the type only for older fixtures.
   */
  recurring?: { id: string; name: string } | null
  version: number
  createdAt: string
  updatedAt: string
}

// ── expense coding by history (AA1, ACCOUNTING_ASSISTANT.md decision 8) ──────────

/** `GET /expenses/suggest-category?payee=` (expenses.create): the payee as typed on the form. */
export const expenseCategorySuggestQuerySchema = z.object({ payee: z.string().trim().min(1).max(200) })
export type ExpenseCategorySuggestQuery = z.input<typeof expenseCategorySuggestQuerySchema>

/** How often a category (and the account it was charged to) was used for the payee. */
export interface ExpensePayeeUse {
  category: { id: string; name: string; nameAr: string | null }
  account: { id: string; code: string; name: string }
  taxRateId: string | null
  count: number
  lastUsedOn: string
}

/**
 * The payee's own history, by rule (no AI): its latest posted expenses (at most 50; the payee matched without
 * regard to case or spacing) grouped by category and account, most used first (then the latest). `category`,
 * `account` and `taxRateId` are the most used pair's, `confidence` its share of those expenses (0–1); all null
 * (confidence 0) for a payee with no history or whose category was archived since. It fills the form; the user
 * saves.
 */
export interface ExpenseCategorySuggestion {
  /** How many past expenses of the payee it rests on; null without expenses.read (AA1 security review). */
  basedOn: number | null
  category: { id: string; name: string; nameAr: string | null } | null
  account: { id: string; code: string; name: string } | null
  taxRateId: string | null
  confidence: number
  uses: ExpensePayeeUse[]
}
